/* ──────────────────────────────────────────────────────────────
   src/pages/reports/profesores/HorasProfesoresContratadasPage.tsx
   Informe: horas semanales contratadas por profesor
   ────────────────────────────────────────────────────────────── */

import { useMemo, useState } from 'react';
import {
  Stack,
  Typography,
  Button,
  LinearProgress,
  Alert,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useHorasProfesoresSemanalesReport } from '../../../hooks/useReportJob';
import type { HorasProfesoresSemanalesRow } from '../../../api/reports';

export default function HorasProfesoresContratadasPage() {
  const { t } = useTranslation<'translation'>();
  const navigate = useNavigate();
  const { run, useStatus } = useHorasProfesoresSemanalesReport();

  const [jobId, setJobId] = useState<string>();
  const [search, setSearch] = useState('');

  const { data: status } = useStatus(jobId);
  const pendiente = !!status && !status.done && !status.failed;

  const rows: HorasProfesoresSemanalesRow[] = status?.result?.rows ?? [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows
      .filter((r) => r.profesor.toLowerCase().includes(q))
      .sort((a, b) => b.horasSemanales - a.horasSemanales || a.profesor.localeCompare(b.profesor));
  }, [rows, search]);

  const handleGenerate = async () => {
    const { jobId } = await run();
    setJobId(jobId);
  };

  const handleGoToProfesor = (profesorId: number) => {
    navigate(`/admin/manager/profesor-datos?profesorId=${profesorId}`);
  };

  return (
    <Stack spacing={3}>
      <Typography variant="h5">{t('horasProfContratadas.titulo')}</Typography>

      <Stack direction="row" spacing={2} alignItems="center">
        <Button variant="contained" onClick={handleGenerate}>
          {t('horasProfContratadas.acciones.generar')}
        </Button>

        {rows.length > 0 && (
          <TextField
            placeholder={t<string>('horasProfContratadas.acciones.buscar')}
            size="small"
            sx={{ ml: 'auto', maxWidth: 260 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        )}
      </Stack>

      {pendiente && status && (
        <Stack spacing={1}>
          <LinearProgress variant="determinate" value={status.progress} />
          <Typography variant="body2">
            {t('horasProfContratadas.estado.generando', { progress: status.progress })}
          </Typography>
        </Stack>
      )}

      {status?.done && (
        <Alert severity="success">
          {t('horasProfContratadas.estado.exito', { count: rows.length })}
        </Alert>
      )}
      {status?.failed && <Alert severity="error">{t('horasProfContratadas.estado.error')}</Alert>}

      {status?.done && (
        <Paper>
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell align="right" sx={{ fontWeight: 'bold', width: 60 }}>
                    {t('horasProfContratadas.tabla.pos')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    {t('horasProfContratadas.tabla.profesor')}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {t('horasProfContratadas.tabla.horas')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((row, idx) => (
                  <TableRow key={row.profesorId} hover>
                    <TableCell align="right">{idx + 1}</TableCell>
                    <TableCell>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => handleGoToProfesor(row.profesorId)}
                        sx={{ textTransform: 'none', p: 0, minWidth: 0 }}
                      >
                        {row.profesor}
                      </Button>
                    </TableCell>
                    <TableCell align="right">{row.horasSemanales}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Stack>
  );
}
